import { Box, Dialog, Divider, Drawer, IconButton, Link, Stack, Typography, useMediaQuery } from "@material-ui/core";
import { alpha, useTheme } from "@material-ui/core/styles";
import { ArrowForward, Close, OpenInNew } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import { PATH_DASHBOARD } from "routes/paths";
import { SettingsSectionView } from "./SettingsSectionView";
import { useSettingsSection } from "./useSettingsSections";
import type { SettingsSectionId } from "./settingsSections";

/**
 * A settings section surfaced where it is USED — the calendar's reminders on the calendar
 * page, virtual numbers on the calls page, minisite design where the minisite is shared.
 *
 * Side panel on desktop, full-screen modal on phones. One host for every such surface,
 * rendering the SAME component the settings page renders (see `settingsSections.tsx`);
 * there is no second copy of any section's UI anywhere.
 *
 * ⚠️ MOUNT THIS INLINE IN THE PAGE TREE. Never open it with `mui-modal-provider`'s
 * `showModal`.
 *
 * `ModalProvider` sits ABOVE `FirestoreProvider` in App.tsx, so anything it mounts is
 * outside `useFirestore`, `useCalendar`, `AppContext` and `CrmSpecifications` — the contexts
 * nearly every section reads. This is the same trap the routing rule documents for the
 * listing dialog ("contexts return undefined and it crashes"). The old
 * `VirtualNumbersSettingsDialog` survived it only by accident: `useVirtualNumbers` reads
 * `useAuth` plus a module-level cache and never touches Firestore context. The moment a
 * panel showed the calendar or minisite sections that accident would have stopped
 * holding, so both calls-page call sites were converted to local state instead.
 *
 * Rendering inline costs a boolean of state at each call site and nothing else: MUI
 * portals the Drawer/Dialog to the body either way, so the panel is not clipped by the
 * page's layout.
 */

/** Matches `CalendarSettingsDrawer`'s old width, which this replaces. */
const PANEL_WIDTH = 460;

type SettingsPanelProps = {
  sectionId: SettingsSectionId;
  open: boolean;
  onClose: VoidFunction;
  /**
   * Hide the "open in full settings" link. For a panel opened FROM the settings page
   * itself, where the link would point at the page it is already on.
   */
  hideFullSettingsLink?: boolean;
  /**
   * Opened while a dialog is open (a wizard, a settings dialog): the desktop drawer takes the
   * dialogs' layer instead of the drawers' one, under every dialog's backdrop otherwise. Mounted
   * after the dialog, it sits on top of it, and anything it opens (a confirmation) sits on top of it.
   */
  overDialog?: boolean;
};

export function SettingsPanel({
  sectionId,
  open,
  onClose,
  hideFullSettingsLink = false,
  overDialog = false
}: SettingsPanelProps): JSX.Element | null {
  const theme = useTheme();
  const { t } = useTranslation();
  // The VIEWPORT, deliberately — this is a portalled overlay over the whole window, so
  // the window is the box it is really painted into. (Contrast `useContactSurfaceDown`,
  // which exists for content laid out inside a sibling column.)
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const section = useSettingsSection(sectionId);

  // Gated away or unknown id. Rendering nothing is right rather than an empty panel: the
  // caller's trigger should already be hidden by the same gate, so reaching here means
  // the two have drifted and a blank panel would be the worse of the two failures.
  if (!section) return null;

  const header = (
    <>
      <Stack direction="row" alignItems="center" gap={1.5} sx={{ px: 2.5, py: 2 }}>
        {isMobile ? (
          <IconButton size="small" onClick={onClose} aria-label={t("common.close")} sx={{ ml: -0.5 }}>
            {/* MUI mirrors this icon under direction:rtl, so ArrowForward points "back"
                in Hebrew and in English alike. */}
            <ArrowForward fontSize="small" />
          </IconButton>
        ) : (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: "primary.main",
              "& svg": { fontSize: 22 }
            }}
          >
            {section.icon}
          </Box>
        )}

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h6" noWrap>
            {t(section.titleKey)}
          </Typography>
          {!hideFullSettingsLink && (
            <Link
              component={RouterLink}
              to={`${PATH_DASHBOARD.general.settings}/${section.id}`}
              variant="caption"
              underline="hover"
              onClick={onClose}
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
            >
              {t("settingsPage.panel.openInSettings")}
              <OpenInNew sx={{ fontSize: 12 }} />
            </Link>
          )}
        </Box>

        {!isMobile && (
          <IconButton size="small" onClick={onClose} aria-label={t("common.close")}>
            <Close fontSize="small" />
          </IconButton>
        )}
      </Stack>
      <Divider />
    </>
  );

  const body = (
    <Box sx={{ px: 2.5, py: 3, overflowY: "auto", flexGrow: 1 }}>
      {/* The sticky header above already names the section, from the same registry row. */}
      <SettingsSectionView section={section} />
    </Box>
  );

  if (isMobile) {
    return (
      <Dialog fullScreen open={open} onClose={onClose}>
        <Stack sx={{ height: "100%" }}>
          {header}
          {body}
        </Stack>
      </Dialog>
    );
  }

  return (
    <Drawer
      // "right" is the INLINE-END side under MUI's RTL mirroring — physical left in
      // Hebrew, physical right in English. That is the side a page's gear button sits on
      // in both directions, so the panel opens from under the control that summoned it.
      anchor="right"
      open={open}
      onClose={onClose}
      sx={overDialog ? { zIndex: (t) => t.zIndex.modal } : undefined}
      PaperProps={{ sx: { width: PANEL_WIDTH, maxWidth: "100vw", display: "flex" } }}
    >
      <Stack sx={{ height: "100%", width: "100%" }}>
        {header}
        {body}
      </Stack>
    </Drawer>
  );
}
